import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Founder / funder password recovery — request step.
 *
 * Backend `/auth/forgot-password` always returns 200 with a generic
 * message (anti-enumeration), so this page ALWAYS shows the same success
 * screen once the request completes — never leaks whether the email is
 * on file.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-md px-4 py-12 sm:px-6 sm:py-16">
        <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]">Clinq</Link>
        <h1 className="mt-6 text-2xl font-bold text-neutral-900 sm:mt-8 sm:text-3xl">
          Forgot your password?
        </h1>

        {sent ? (
          <div data-testid="forgot-success" className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-semibold">Check your inbox.</p>
            <p className="mt-1 text-emerald-800">
              If an account exists for <b>{email}</b>, we&apos;ve sent a
              password reset link. The link expires in 30 minutes.
            </p>
            <p className="mt-3 text-xs text-emerald-700">
              Didn&apos;t get it? Check your spam folder, or try again in a
              minute (we rate-limit resets to 3 per hour per email).
            </p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm text-neutral-600">
              Enter the email you signed up with. We&apos;ll send you a link
              to set a new password.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input
                data-testid="forgot-email"
                type="email"
                placeholder="you@example.com"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]"
              />
              {err && (
                <p data-testid="forgot-error" className="text-sm text-red-600">
                  {String(err)}
                </p>
              )}
              <button
                data-testid="forgot-submit"
                disabled={busy}
                type="submit"
                className="w-full rounded-lg bg-[#0A8B85] py-3 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-sm text-neutral-600">
          Remembered it?{" "}
          <Link to="/login" data-testid="forgot-back-to-login"
                className="font-semibold text-[#0A8B85] hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
